import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import './organizations.css';
import { ROUTES, organizationEditPath, organizationTeamPath } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../shared/utils/dateFormat';
import { OrganizationV2 } from '../../../types/organization';
import { formatOwnerLabel } from '../../../utils/formatOwnerLabel';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { StatusBadgeCell, TableActionButton } from '../components/cells';

interface OrganizationsTableProps {
  organizations: OrganizationV2[];
}

/** The platform-admin premium page for one organization. */
export const organizationPremiumPath = (organizationId: string) =>
  `/${ROUTES.DASHBOARD}/organizations/${organizationId}/premium`;

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
export const ORGANIZATIONS_TABLE_MIN_WIDTH = '1100px';

export const ORGANIZATIONS_COLUMNS = [
  { key: 'name', labelKey: 'dashboard.organizations.columnName', width: '22%', cellClassName: 'cell-id', skeletonVariant: 'address' },
  { key: 'owner', labelKey: 'dashboard.organizations.columnOwner', width: '26%', skeletonVariant: 'address' },
  { key: 'stations', labelKey: 'dashboard.organizations.columnStations', width: '11%', align: 'center', skeletonVariant: 'id' },
  { key: 'monobank', labelKey: 'dashboard.organizations.columnMonobank', width: '16.5%', skeletonVariant: 'status' },
  { key: 'created', labelKey: 'dashboard.organizations.columnCreated', width: '14%', skeletonVariant: 'id' },
  { key: 'actions', labelKey: 'dashboard.tables.actions', width: '10.5%', align: 'right', skeletonVariant: 'id' },
] as const satisfies readonly DataTableColumnMeta[];

export const OrganizationsTable = memo(function OrganizationsTable({
  organizations,
}: OrganizationsTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { has } = usePermissions();

  const renderers = useMemo<DataTableRenderers<OrganizationV2, typeof ORGANIZATIONS_COLUMNS>>(
    () => ({
      name: (org) => (
        <Link to={organizationEditPath(org.id)} className="org-name-link">
          {org.name}
        </Link>
      ),
      owner: (org) => formatOwnerLabel(org.owner),
      stations: (org) => (
        <Link
          to={`/${ROUTES.DASHBOARD}/organizations/${org.id}/stations`}
          className="permission-group-toggle"
        >
          {org.stationCount}
        </Link>
      ),
      monobank: (org) => (
        <>
          <StatusBadgeCell
            badgeClass={org.hasMonobankToken ? 'status-badge-active' : 'status-badge-inactive'}
            label={
              org.hasMonobankToken
                ? t('dashboard.organizations.connected')
                : t('dashboard.organizations.notConnected')
            }
          />
          {org.hasMonobankToken && org.monobankTokenHint && (
            <span className="org-token-hint org-text-muted">{org.monobankTokenHint}</span>
          )}
        </>
      ),
      created: (org) => formatDate(org.createdAt ?? ''),
      actions: (org) => (
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          {has('organizations.manage') && (
            <TableActionButton
              icon="groups"
              onClick={() => navigate(organizationTeamPath(org.id))}
              ariaLabel={t('dashboard.organizations.teamAction')}
              title={t('dashboard.organizations.teamAction')}
            />
          )}
          <TableActionButton
            icon="workspace_premium"
            onClick={() => navigate(organizationPremiumPath(org.id))}
            ariaLabel={t('dashboard.premium.orgAria', { name: org.name })}
            title={t('dashboard.premium.orgTitle')}
          />
          <TableActionButton
            icon="edit"
            onClick={() => navigate(organizationEditPath(org.id))}
            ariaLabel={t('dashboard.organizations.editAria', { name: org.name })}
            title={t('dashboard.organizations.edit')}
          />
        </div>
      ),
    }),
    [t, navigate, has],
  );

  return (
    <DataTable
      columns={ORGANIZATIONS_COLUMNS}
      renderers={renderers}
      rows={organizations}
      rowKey={(org) => org.id}
      minWidth={ORGANIZATIONS_TABLE_MIN_WIDTH}
    />
  );
});

export default OrganizationsTable;
